import { Component } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  FlowToolsService,
  PriceSimulatorResponse,
} from '@core/services/flow-tools/flow-tools.service';

/**
 * Ejecuta en el CMS la misma calculadora que dispara el WhatsApp Flow
 * "Price Simulator" (PRICE_CO_TOKEN) — ver
 * backend_v1/utils/flows/flowConectadas/flowPriceSimulator.js — llamando
 * directo a POST /chatBot/flowprice, sin pasar por WhatsApp.
 */
@Component({
  selector: 'app-price-simulator-tester',
  templateUrl: './price-simulator-tester.component.html',
  styleUrls: ['./price-simulator-tester.component.scss'],
})
export class PriceSimulatorTesterComponent {
  form: FormGroup;
  isLoading = false;
  errorMessage = '';
  result: PriceSimulatorResponse['result'] | null = null;

  languageOptions = [
    { value: 'co', label: 'Colombia (COP)' },
    { value: 'mx', label: 'México (MXN)' },
  ];

  constructor(
    private fb: FormBuilder,
    private flowToolsService: FlowToolsService,
  ) {
    this.form = this.fb.group({
      language: ['co'],
      offer_type: ['products', Validators.required],
      product_type: ['elaborated'],

      // Producto elaborado
      elab_materials: [''],
      elab_hours: [''],
      elab_value_hour: [''],
      elab_rent_option: ['no'],
      elab_rent_monthly: [''],
      elab_rent_hours: [''],

      // Producto comprado/revendido
      purchased_cost: [''],
      purchased_hours: [''],
      purchased_value_hour: [''],
      purchased_rent_option: ['no'],
      purchased_rent_monthly: [''],
      purchased_rent_hours: [''],

      // Servicios
      service_hours: [''],
      service_value_hour: [''],
      rent_yes_no: ['no'],
      rent_monthly: [''],
      rent_hours: [''],
      // Nota: el backend espera literalmente 'yes'/'no' en estos 3 campos
      // (flowPriceSimulator.js:396), no 'si'/'no'.

      // Otros costos (hasta 4)
      other_cost_1_name: [''],
      other_cost_1_value: [''],
      other_cost_2_name: [''],
      other_cost_2_value: [''],
      other_cost_3_name: [''],
      other_cost_3_value: [''],
      other_cost_4_name: [''],
      other_cost_4_value: [''],

      final_margin: ['', Validators.required],
    });
  }

  get isProducts(): boolean {
    return this.form.get('offer_type')?.value === 'products';
  }

  get isElaborated(): boolean {
    return this.isProducts && this.form.get('product_type')?.value === 'elaborated';
  }

  get isPurchased(): boolean {
    return this.isProducts && this.form.get('product_type')?.value === 'purchased';
  }

  get isServices(): boolean {
    return this.form.get('offer_type')?.value === 'services';
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.errorMessage = 'El tipo de oferta y el margen final son obligatorios.';
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';
    this.result = null;

    const raw = this.form.value;
    const payload: Record<string, any> = {};
    Object.keys(raw).forEach((key) => {
      if (raw[key] !== '' && raw[key] !== null) {
        payload[key] = raw[key];
      }
    });

    this.flowToolsService.simulatePrice(payload as any).subscribe(
      (response) => {
        this.isLoading = false;
        if (response.ok) {
          this.result = response.result || null;
        } else {
          this.errorMessage = response.msg;
        }
      },
      (error) => {
        this.isLoading = false;
        this.errorMessage = `Error: ${error.error?.msg || error.message}`;
      },
    );
  }

  reset(): void {
    this.form.reset({
      language: 'co',
      offer_type: 'products',
      product_type: 'elaborated',
      elab_rent_option: 'no',
      purchased_rent_option: 'no',
      rent_yes_no: 'no',
    });
    this.result = null;
    this.errorMessage = '';
  }
}
